import React, { memo, useCallback } from 'react';
import { bool, func } from 'prop-types';
import {
  HeartInactive,
  User,
  Document,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { urls } from 'constants/urls';
import AnchorLink from 'components/AnchorLink';
import { handleLoginWithRedirect } from 'utils/login';

import ShoppingSummaryContainer from '../ShoppingSummary';

import MyAccount from './MyAccount/MyAccount';

import styles from './LinksBar.scss';
import SlotButton from '../SlotButton';

const LinksBar = memo(
  ({
    isXLDesktop,
    isDesktop,
    isMobile,
    showContactAddressNotPresentNotification,
    currentPageIsGroceriesHomePage,
    isLoggedIn,
    onToggleSeasonalMenu,
    isSeasonalMenuOpen,
    scrolled,
  }) => {
    const { trackEvent } = useAnalytics();

    const pushMenuClick = useCallback(
      buttonLabel => {
        trackEvent({
          event: 'click_megamenu',
          mega_menu: {
            button_clicked: buttonLabel,
          },
        });
      },
      [trackEvent],
    );

    const unauthenticatedAccountLinks = (
      <div aria-label="Sign in and register">
        <AnchorLink
          className={styles.links}
          href={urls.login}
          onClick={handleLoginWithRedirect}
          underline="never"
        >
          <User size="small" />
          <span className={styles.anchorText} data-testid="signIn">
            Sign in
          </span>
        </AnchorLink>
      </div>
    );

    const authenticatedAccountLinks = (
      <div aria-label="Account links" className={styles.authenticadLinks}>
        {(isDesktop || isXLDesktop) && (
          <AnchorLink
            id="site-header-favourites"
            to={urls.favourites}
            className={styles.links}
            onClick={() => pushMenuClick('Favourites')}
          >
            <HeartInactive className={styles.favouritesIcon} size="small" />
            <span className={styles.anchorText}>Favourites</span>
          </AnchorLink>
        )}
        {isXLDesktop && (
          <AnchorLink
            id="site-header-orders"
            to={urls.myOrdersPage}
            className={styles.links}
            onClick={() => pushMenuClick('Orders')}
          >
            <Document className={styles.favouritesIcon} size="small" />
            <span className={styles.anchorText}>Orders</span>
          </AnchorLink>
        )}
        {!isMobile && <MyAccount />}
      </div>
    );

    const accountLinks = isLoggedIn ? authenticatedAccountLinks : unauthenticatedAccountLinks;

    return (
      <div className={styles.linksBar}>
        {accountLinks}
        <SlotButton />
        {(isLoggedIn || !isMobile) && <ShoppingSummaryContainer scrolled={scrolled} />}
      </div>
    );
  },
);

LinksBar.displayName = 'LinksBar';

LinksBar.propTypes = {
  showContactAddressNotPresentNotification: bool,
  currentPageIsGroceriesHomePage: bool,
  isLoggedIn: bool,
  isSeasonalMenuOpen: bool,
  onToggleSeasonalMenu: func.isRequired,
  isXLDesktop: bool,
  isDesktop: bool,
  isMobile: bool,
  scrolled: bool,
};

LinksBar.defaultProps = {
  showContactAddressNotPresentNotification: false,
  currentPageIsGroceriesHomePage: false,
  isLoggedIn: false,
  isSeasonalMenuOpen: false,
  isXLDesktop: false,
  isDesktop: false,
  isMobile: false,
  scrolled: false,
};

export default LinksBar;
